import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '../../lib/api';
import { useAuth } from '../../lib/auth';

const label = { fontSize: 13, fontWeight: 700, color: 'var(--ink-2)' } as const;
const input = {
  minHeight: 50,
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0 14px',
  fontSize: 16,
  color: 'var(--ink)',
  background: 'var(--card)',
  width: '100%',
} as const;

/** Login (10.3): logo 改 + Kaizen, lema, 改善 vertical, pilares, e-mail e senha, Google se configurado. */
export default function LoginPage() {
  const { user, login } = useAuth();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(params.get('erro') === 'google' ? 'Não deu pra entrar com o Google' : null);
  const [busy, setBusy] = useState(false);
  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => get<{ google: boolean; signup: boolean }>('/auth/providers'),
    staleTime: Infinity,
  });

  useEffect(() => {
    document.title = 'Entrar · Kaizen';
  }, []);

  if (user) {
    const from = (loc.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sem conexão. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div style={{ padding: '44px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <span
            lang="ja"
            aria-hidden="true"
            style={{
              width: 46, height: 46, borderRadius: 6, background: 'var(--shu)', color: 'var(--paper)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 800,
            }}
          >
            改
          </span>
          <span style={{ fontFamily: 'var(--font-serif)', fontSize: 44, lineHeight: 1, fontWeight: 800, letterSpacing: -0.5 }}>
            Kaizen
          </span>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: 'var(--ink-2)' }}>Um pouco melhor, todo dia.</p>
          <p lang="ja" style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 15, color: 'var(--shu)', fontWeight: 700 }}>
            毎日、少しずつ。
          </p>
        </div>
        <span
          lang="ja"
          aria-hidden="true"
          style={{
            fontFamily: 'var(--font-serif)', fontSize: 64, lineHeight: 1.05, fontWeight: 800, color: 'var(--ink)',
            writingMode: 'vertical-rl', letterSpacing: 6, opacity: 0.9,
          }}
        >
          改善
        </span>
      </div>

      <div
        style={{
          margin: '26px 28px 0', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
        }}
      >
        {[
          ['食', 'Comida'],
          ['練', 'Treino'],
          ['学', 'Estudo'],
        ].map(([k, l], i) => (
          <div
            key={l}
            style={{
              padding: '12px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              borderRight: i < 2 ? '1px solid var(--border)' : undefined,
            }}
          >
            <span lang="ja" aria-hidden="true" style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 800 }}>
              {k}
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-2)' }}>{l}</span>
          </div>
        ))}
      </div>

      <form
        onSubmit={submit}
        noValidate
        style={{ marginTop: 'auto', padding: '26px 28px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <h1 style={{ margin: 0, fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 800 }}>Entrar</h1>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="email" style={label}>
            E-mail
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="voce@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={Boolean(error)}
            style={input}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor="senha" style={label}>
            Senha
          </label>
          <div style={{ position: 'relative', display: 'flex' }}>
            <input
              id="senha"
              type={visible ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'login-erro' : undefined}
              style={{ ...input, flex: 1, padding: '0 92px 0 14px' }}
            />
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              aria-pressed={visible}
              style={{
                position: 'absolute', right: 3, top: 3, height: 44, padding: '0 12px', border: 0, borderRadius: 4,
                background: 'transparent', color: 'var(--shu)', fontSize: 13, fontWeight: 700,
              }}
            >
              {visible ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </div>
        {error && (
          <p id="login-erro" role="alert" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--shu)' }}>
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          style={{
            marginTop: 4, minHeight: 52, borderRadius: 6, background: 'var(--ink)', color: 'var(--paper)', border: 0,
            fontSize: 16, fontWeight: 700, letterSpacing: 1, opacity: busy ? 0.7 : 1,
          }}
        >
          Entrar
        </button>
        {providers.data?.google && (
          <a
            href="/api/auth/google/start"
            style={{
              minHeight: 50, borderRadius: 6, border: '1px solid var(--border)', background: 'transparent',
              color: 'var(--ink)', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center',
              justifyContent: 'center', textDecoration: 'none',
            }}
          >
            Continuar com Google
          </a>
        )}
      </form>
    </div>
  );
}
